import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { AnalyticsAnalyticEventAttribute, AnalyticsCreateAnalyticEventDto } from '../../../../../libs/shared/generated/src/lib/types/analytics';
import { EnvironmentService } from '../@core/services/environment.service';

/**
 * Configuration for analytics event tracking
 */
export interface AnalyticsEventConfig {
  /** The item ID for the event (e.g., content ID, post ID, etc.) */
  itemId?: string;
  /** Additional attributes to include with the event */
  attributes?: Record<string, string>;
}


@Injectable({
  providedIn: 'root',
})
export class AnalyticsEventsService {
  private readonly apiUrl: string;
  private readonly appVersion: string;
  private readonly platform = 'WEB';

  constructor(
    private readonly http: HttpClient,
    private readonly environmentService: EnvironmentService
  ) {
    this.apiUrl = this.environmentService.analyticsServiceUrl;
    this.appVersion = this.environmentService.appVersion;
  }

  /**
   * Track a single analytics event
   */
  trackEvent(
    type: AnalyticsCreateAnalyticEventDto['type'],
    config: AnalyticsEventConfig = {}
  ): void {
    const event = this.createEvent(type, config);
    this.sendAnalyticEvents([event]);
  }

  /**
   * Track multiple analytics events in batch
   */
  trackEvents(events: Array<{ type: AnalyticsCreateAnalyticEventDto['type']; config: AnalyticsEventConfig }>): void {
    const analyticsEvents = events.map(({ type, config }) => this.createEvent(type, config));
    this.sendAnalyticEvents(analyticsEvents);
  }


  /**
   * Track a view event (one-time event when user views something)
   */
  trackView(
    type: AnalyticsCreateAnalyticEventDto['type'],
    itemId: string,
    additionalConfig: Omit<AnalyticsEventConfig, 'itemId'> = {}
  ): void {
    this.trackEvent(type, {
      ...additionalConfig,
      itemId,
    });
  }

  /**
   * Track an action event (one-time event when user performs an action)
   */
  trackAction(
    type: AnalyticsCreateAnalyticEventDto['type'],
    itemId?: string,
    additionalConfig: Omit<AnalyticsEventConfig, 'itemId'> = {}
  ): void {
    this.trackEvent(type, {
      ...additionalConfig,
      ...(itemId && { itemId }),
    });
  }

  // ===== SPECIFIC EVENT TRACKING METHODS =====

  // Web Content Events
  trackWebContentView(itemId: string): void {
    this.trackView('WEBCONTENT_VIEW', itemId);
  }

  // Community Events
  trackCommunityView(itemId: string): void {
    this.trackView('COMMUNITY_VIEW', itemId);
  }

  trackCommunityPostView(itemId: string): void {
    this.trackView('COMMUNITY_POST_VIEW', itemId);
  }

  // Onboarding Events
  trackOnboardingPathView(itemId: string): void {
    this.trackView('ONBOARDING_PATH_VIEW', itemId);
  }

  trackOnboardingContentView(itemId: string): void {
    this.trackView('ONBOARDING_CONTENT_VIEW', itemId);
  }

  // Board Events
  trackBoardPostView(itemId: string): void {
    this.trackView('BOARD_POST_VIEW', itemId);
  }

  // Event Events
  trackEventView(itemId: string): void {
    this.trackView('EVENT_VIEW', itemId);
  }

  trackEventOccurrenceSubscription(itemId: string): void {
    this.trackAction('EVENT_OCCURRENCE_SUBSCRIPTION', itemId);
  }

  trackEventOccurrenceUnsubscription(itemId: string): void {
    this.trackAction('EVENT_OCCURRENCE_UNSUBSCRIPTION', itemId);
  }

  // Repository Events
  trackRepositoryDocumentView(itemId: string): void {
    this.trackView('REPOSITORY_DOCUMENT_VIEW', itemId);
  }

  trackRepositoryDocumentDownload(itemId: string): void {
    this.trackAction('REPOSITORY_DOCUMENT_DOWNLOAD', itemId);
  }

  // Contest Events
  trackContestView(itemId: string): void {
    this.trackView('CONTEST_VIEW', itemId);
  }

  trackContestGameView(itemId: string): void {
    this.trackView('CONTEST_GAME_VIEW', itemId);
  }

  // Chat Events
  trackChatView(itemId: string): void {
    this.trackView('CHAT_VIEW', itemId);
  }

  // Survey Events
  trackSurveyView(itemId: string): void {
    this.trackView('SURVEY_VIEW', itemId);
  }

  trackSurveyStart(itemId: string): void {
    this.trackAction('SURVEY_START', itemId);
  }

  trackSurveyComplete(itemId: string): void {
    this.trackAction('SURVEY_COMPLETE', itemId);
  }

  // Feedback Events
  trackFeedbackInstantView(itemId: string): void {
    this.trackView('FEEDBACK_INSTANT_VIEW', itemId);
  }

  trackFeedbackInstantComplete(itemId: string): void {
    this.trackAction('FEEDBACK_INSTANT_COMPLETE', itemId);
  }

  trackFeedbackOpenView(): void {
    this.trackAction('FEEDBACK_OPEN_VIEW');
  }

  trackFeedbackOpenComplete(itemId: string): void {
    this.trackAction('FEEDBACK_OPEN_COMPLETE', itemId);
  }

  // Profile Events
  trackProfileAboutMeView(): void {
    this.trackAction('PROFILE_ABOUT_ME_VIEW');
  }

  trackProfileSelfServiceView(itemId: string): void {
    this.trackView('PROFILE_SELF_SERVICE_VIEW', itemId);
  }

  trackProfileQRCodeView(): void {
    this.trackAction('PROFILE_QRCODE_VIEW');
  }

  trackProfileBarcodeView(): void {
    this.trackAction('PROFILE_BARCODE_VIEW');
  }

  trackProfileAboutMeUpdate(): void {
    this.trackAction('PROFILE_ABOUT_ME_UPDATE');
  }

  trackProfileSelfServiceAction(itemId: string): void {
    this.trackAction('PROFILE_SELF_SERVICE_ACTION', itemId);
  }

  // Notification Events
  trackNotificationOpen(itemId: string): void {
    this.trackAction('NOTIFICATION_OPEN', itemId);
  }

  // ===== PRIVATE METHODS =====

  private createEvent(
    type: AnalyticsCreateAnalyticEventDto['type'],
    config: AnalyticsEventConfig
  ): AnalyticsCreateAnalyticEventDto {
    const attributes: AnalyticsAnalyticEventAttribute[] = [];

    // Add itemId as attribute if provided
    if (config.itemId) {
      attributes.push({ key: 'itemId', value: config.itemId });
    }

    // Add custom attributes
    if (config.attributes) {
      attributes.push(...Object.entries(config.attributes).map(([key, value]) => ({ key, value })));
    }

    return {
      uuid: uuidv4(),
      type,
      platform: this.platform,
      version: this.appVersion,
      createdAt: new Date().toISOString(),
      attributes,
      metrics: [],
    };
  }


  private sendAnalyticEvents(events: AnalyticsCreateAnalyticEventDto[]): void {
    // Skip if API URL is not configured
    if (!this.apiUrl) {
      return;
    }

    try {
      this.http.post(`${this.apiUrl}`, events).subscribe({
        next: () => { },
        error: (error) => console.error('Analytics API error:', error)
      });
    } catch (error) {
      console.error('Error preparing analytics events:', error);
    }
  }
}
